import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { PATH_TO_WARNING_AUDIO, DATA_CHANNELS, getRobotIconPath } from "shared/config/setting";
import { message, Select, Input, Button, Tooltip } from "antd";
import { AudioOutlined, StopOutlined, SendOutlined, CloseOutlined} from "@ant-design/icons";
import { COMMAND_TOPICS, STATUS_TOPICS } from "shared/services/messaging/messageTypes";
import { useSpeechToText } from "features/robot-teleop/hooks/useSpeechToText";

import GamepadController from "features/robot-teleop/components/joystick/JoystickController";
import TwistKeyboardMoveCommander from "features/robot-teleop/components/keyboard/TwistKeyboardMoveCommander";
import RobotStatusViewer from "features/robot-teleop/components/robot-status/status-ctx/robot-status";
import JitterGraph from "features/robot-teleop/components/JitterGraph";
import "shared/assets/styles/components/demobot-control-page.css";
import { useServerConnection } from "shared/contexts/ServerConnectionContext";
import LogViewer from "shared/components/ui/LogViewer";
import { useUserData } from "shared/contexts/UserDataProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";
import RobotPeerVideoViewer from "features/robot-teleop/components/robot-video-viewer";
import {
  RobotPeerConnectionProvider,
  useRobotPeerConnection,
} from "features/robot-teleop/contexts/RobotConnectionContext";
import JoyKeyboardTopicCommander from "features/robot-teleop/components/keyboard/JoyKeyboardTopicCommander";
import { usePageTitle } from "shared/contexts/PageTitleContext";
import { useRobotStatus } from "features/robot-teleop/hooks/useRobotStatus";
import { useRobotCommands } from "features/robot-teleop/hooks/useRobotCommands";
import { useEventSubscription, getRobotEvent } from "shared/services/eventBus";
import { useRobotControlManager } from "features/robot-teleop/hooks/useRobotInfoService";
import {
  normalizeRobotStatus,
  ROBOT_STATUS_COMPARE_KEYS,
} from "features/robot-teleop/utils/robotStatus";
import logger from "shared/utils/logging";

// Map the per-peer WebRTC connection state (plus the robot's online flag from
// the signaling server) to a status key used for the dot colour and its text.
const getPeerConnectionStatus = (connectionState, robotOnlineStatus) => {
  if (robotOnlineStatus === false) return { key: "offline", text: "Offline" };
  switch (connectionState) {
    case "connected":
      return { key: "connected", text: "Connected" };
    case "disconnected":
      return { key: "disconnected", text: "Disconnected" };
    case "failed":
      return { key: "failed", text: "Failed" };
    case "closed":
      return { key: "closed", text: "Closed" };
    case "new":
    case "connecting":
      return { key: "connecting", text: "Connecting" };
    default:
      // No peer connection yet: the provider dials as soon as the robot is online.
      return robotOnlineStatus
        ? { key: "connecting", text: "Connecting" }
        : { key: "unknown", text: "Unknown" };
  }
};

// Small overlay badge rendered at the top of each camera tile: a connection
// indicator dot, a label (peer id or a short name) and the connection state.
// It must be rendered inside the RobotPeerConnectionProvider of the peer it
// describes, which is the case for every tile in the cameras panel below.
const PeerTileLabel = ({ label }) => {
  const { connectionState, robotOnlineStatus } = useRobotPeerConnection();
  const status = getPeerConnectionStatus(connectionState, robotOnlineStatus);

  return (
    <div className="camera-peer-label" title={`${label} - ${status.text}`}>
      <span className={`peer-status-dot ${status.key} connection-indicator`} />
      <span className="peer-id">{label}</span>
      <span className="peer-status-text">{status.text}</span>
    </div>
  );
};

const RobotGroupControlPage = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const { id } = useParams();
  const { updatePageTitle } = usePageTitle();
  const { notifyError, notifyInfo } = useNotification();

  // Set page title
  useEffect(() => {
    updatePageTitle(`${id} | Control`);
  }, [id]);

  const {
    connectionState,
    CONNECTION_STATES,
    error: signal_Error,
    reconnectCountDown,
  } = useServerConnection();

  const { userDataState } = useUserData();
  const robotGroups = userDataState.robotGroups;
  const robotGroupInfo = robotGroups[id];
  const peerConnections = robotGroupInfo?.connections;
  const controlPeerId = robotGroupInfo?.control_connections[0];
  const { loading, robotInfo: robot, robotClients, loadRobotInfo, loadRobotClientsInfo } = useRobotControlManager(id);
  const joyControlMapping = robot?.robot_type?.joy_control_mapping ?? null;

  const audioAlarm = useRef(null);

  const [connectionLogRecords, setConnectionLogRecords] = useState([]);
  const logRecordsRef = useRef([]);
  const logDisplaySize = 10;
  const maxLogRecords = logDisplaySize;

  // Throttle control for addLogRecord
  const lastLogTimeRef = useRef(0);
  const logUpdateTimerRef = useRef(null);
  const logThrottleDelay = 500;

  const {
    setRobotId,
    connectionState: rtcConnectionState,
    mediaTracks,
    channelSend,
    connect,
    error,
    robotOnlineStatus,
  } = useRobotPeerConnection();


  // ===== ROBOT STATUS MANAGEMENT =====
  // The robot publishes a `robot.status` topic ({ robot_info, robot_state, mission_info })
  // over the data channel; RobotConnectionContext re-publishes it on the event bus as
  // `robot:<controlPeerId>:robot.status`. normalizeRobotStatus keeps that structure (plus
  // display values under `derived`) for RobotStatusViewer.
  const [robotErrorInfo, setRobotErrorInfo] = useState(null);

  const robotStatusOptions = useMemo(
    () => ({
      throttle: 100, // Throttle status updates to every 100ms
      topic_name: STATUS_TOPICS.ROBOT_STATUS, // 'robot_status' or 'robot.status'
      transformFn: normalizeRobotStatus,
      compareKeys: ROBOT_STATUS_COMPARE_KEYS, // Re-render only when a displayed field changes
    }),
    []
  );

  // Use the useRobotStatus hook with built-in throttling
  const { status: robotStatus } = useRobotStatus(controlPeerId, robotStatusOptions);

  // Use the new useRobotCommands hook
  const { sendCommand, sendTwist, sendJoy, sendKeyboard } = useRobotCommands(
    channelSend,
    {
      channelLabel: DATA_CHANNELS.CHAT,
      onError: (error) => {
        notifyError(`Command failed: ${error.message}`);
      },
    }
  );

  // ===== LOG RECORD FUNCTION =====
  const addLogRecord = useCallback(
    (msg) => {
      // Rate limiting for log messages
      const now = Date.now();
      if (now - lastLogTimeRef.current < 100) {
        // Skip if less than 100ms since last log
        return;
      }

      const trimmedMsg = msg.length > 100 ? msg.slice(0, 100) + "..." : msg;
      const logRecord = { Time: now, Message: trimmedMsg };

      // Use functional update to avoid dependency on current state
      logRecordsRef.current = [
        ...logRecordsRef.current.slice(-(maxLogRecords - 1)),
        logRecord,
      ];

      const waitingTime = logThrottleDelay - (now - lastLogTimeRef.current);

      if (waitingTime > 0) {
        if (logUpdateTimerRef.current) {
          return;
        }

        logUpdateTimerRef.current = setTimeout(() => {
          setConnectionLogRecords([...logRecordsRef.current]);
          lastLogTimeRef.current = Date.now();
          logUpdateTimerRef.current = null;
        }, waitingTime);
        return;
      }

      if (logUpdateTimerRef.current) {
        clearTimeout(logUpdateTimerRef.current);
        logUpdateTimerRef.current = null;
      }
      lastLogTimeRef.current = now;
      setConnectionLogRecords([...logRecordsRef.current]);
    },
    [maxLogRecords, logThrottleDelay]
  );

  const robotLogMessageEventPath = useMemo(
    () => getRobotEvent(controlPeerId, "log_message"),
    [controlPeerId]
  );

  const robotLogMessageHandler = useCallback((value, topicName) => {
    const displayValue = typeof value === "object" ? JSON.stringify(value) : String(value);
    const truncatedValue = displayValue.length > 200 ? displayValue.slice(0, 200) + "..." : displayValue;
    logger.log("[" + topicName + "] " + displayValue);
    addLogRecord("[" + topicName + "] " + truncatedValue);
  }, [addLogRecord]);

  useEventSubscription(
    robotLogMessageEventPath,
    robotLogMessageHandler,
    {},
    [robotLogMessageEventPath, robotLogMessageHandler]
  );

  // ===== SETUP EFFECT =====
  useEffect(() => {
    logger.log("Robot controlPeerId: ", controlPeerId);
    setRobotId(controlPeerId);
  }, [controlPeerId, setRobotId]);

  // ===== CLEANUP EFFECT =====
  useEffect(() => {
    return () => {
      if (logUpdateTimerRef.current) {
        clearTimeout(logUpdateTimerRef.current);
        logUpdateTimerRef.current = null;
      }
    };
  }, []);

  const playAlarm = (play) => {
    const audio = audioAlarm.current;
    if (play) audio.play();
    else audio.pause();
  };

  useEffect(() => {
    if (!robotErrorInfo) playAlarm(false);
    else playAlarm(true);
  }, [robotErrorInfo]);

  // Control mode: 'twist' (TwistKeyboardMoveCommander), 'joy' (JoyKeyboardTopicCommander), 'gamepad' (GamepadController)
  const [controlMode, setControlMode] = useState("joy");
  const [lastSentText, setLastSentText] = useState("");

  // ===== SPEECH TO TEXT ======
  const {
    textInput: sttText,
    setTextInput: setSttText,
    interimText: sttInterimText,
    isRecording: sttIsRecording,
    isSpeechSupported,
    toggleRecording: sttToggleRecording,
    startRecording: sttStartRecording,
    stopRecording: sttStopRecording,
    lang: sttLang,
    setLang: setSttLang,
  } = useSpeechToText({
    initialLang: "ko-KR",
    onError: (msg) => notifyError(msg),
  });

  // Refs kept in sync so event handlers never read stale closures
  const sttIsRecordingRef = useRef(sttIsRecording);
  useEffect(() => { sttIsRecordingRef.current = sttIsRecording; }, [sttIsRecording]);

  const sttTextRef = useRef(sttText);
  useEffect(() => { sttTextRef.current = sttText; }, [sttText]);

  const rtcConnectionStateRef = useRef(rtcConnectionState);
  useEffect(() => { rtcConnectionStateRef.current = rtcConnectionState; }, [rtcConnectionState]);

  // Track whether the ongoing recording was started by the PTT key
  const sttPTTActiveRef = useRef(false);

  // ===== PUSH-TO-TALK: hold ` (backtick) to record =====
  const PTT_KEY_CODE = "Backquote";
  const TOGGLE_STT_KEY_CODE = "KeyP";
  const RESET_INSTRUCTION_KEY_CODE = "BracketRight";
  const SEND_INSTRUCTION_KEY_CODE = "Backslash";

  useEffect(() => {
    if (!isSpeechSupported) return;

    const handleKeyDown = (e) => {
      if (e.code !== PTT_KEY_CODE) return;
      if (e.repeat) return;
      // Don't hijack focus when the user is typing in an input/textarea
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (sttIsRecordingRef.current) return;
      sttPTTActiveRef.current = true;
      sttStartRecording();
    };

    const handleKeyUp = (e) => {
      if (e.code !== PTT_KEY_CODE) return;
      if (!sttPTTActiveRef.current) return;
      sttPTTActiveRef.current = false;
      sttStopRecording();
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [isSpeechSupported, sttStartRecording, sttStopRecording]);

  // ===== SHARED SEND HANDLER =====
  const handleSendInstruction = useCallback((text) => {
    if (!text?.trim()) return;
    if (rtcConnectionStateRef.current !== "connected") return;
    sendCommand(COMMAND_TOPICS.NLP_INSTRUCTION, { text: text.trim(), lang: sttLang });
    setLastSentText(text.trim());
    setSttText("");
  }, [sendCommand, sttLang, setSttText]);

  const handleSendInstructionRef = useRef(handleSendInstruction);
  useEffect(() => { handleSendInstructionRef.current = handleSendInstruction; }, [handleSendInstruction]);

  // ===== RESET HANDLER =====
  const handleResetInstruction = useCallback(() => {
    if (sttIsRecordingRef.current) sttStopRecording();
    setSttText("");
  }, [sttStopRecording, setSttText]);

  const handleResetInstructionRef = useRef(handleResetInstruction);
  useEffect(() => { handleResetInstructionRef.current = handleResetInstruction; }, [handleResetInstruction]);

  // ===== TOGGLE STT SHORTCUT: press P to toggle recording =====
  useEffect(() => {
    if (!isSpeechSupported) return;

    const handleKeyDown = (e) => {
      if (e.code !== TOGGLE_STT_KEY_CODE) return;
      if (e.repeat) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      sttToggleRecording();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSpeechSupported, sttToggleRecording]);

  // ===== SEND SHORTCUT: press \ to send NLP instruction =====
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code !== SEND_INSTRUCTION_KEY_CODE) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      handleSendInstructionRef.current(sttTextRef.current);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // ===== RESET SHORTCUT: press ] to clear the text box =====
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code !== RESET_INSTRUCTION_KEY_CODE) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      handleResetInstructionRef.current();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleOnCommandCreated = useCallback(
    (command, quite = false) => {
      if (!channelSend) {
        notifyError("Data channel is not ready!");
        return;
      }

      const success = sendTwist(command);

      if (success) {
        if (!quite) {
          messageApi.open({
            type: "info",
            content: "Command has been sent to robot!",
            style: { marginTop: "10vh" },
          });
        }
        addLogRecord(`cmd_vel sent: ${JSON.stringify(command)}`);
      }
    },
    [channelSend, sendTwist, messageApi, addLogRecord, notifyError]
  );

  const handleKeyboardTopicCreated = useCallback(
    (topicValue, quite = false) => {
      if (!channelSend) {
        notifyError("Data channel is not ready!");
        return;
      }

      const success = sendKeyboard(topicValue);

      if (success) {
        if (!quite) {
          messageApi.open({
            type: "info",
            content: "Command has been sent to robot!",
            style: { marginTop: "10vh" },
          });
        }
        addLogRecord(`keyboard sent: ${JSON.stringify(topicValue)}`);
      }
    },
    [channelSend, sendKeyboard, messageApi, addLogRecord, notifyError]
  );

  const handleOnJoystickCommandCreated = useCallback(
    (joyMsg, quite = false) => {
      if (!channelSend || rtcConnectionState !== "connected") {
        notifyError("Connection is not ready!");
        return;
      }

      const success = sendJoy(joyMsg);

      if (success) {
        if (!quite) {
          messageApi.open({
            type: "info",
            content: "Command has been sent to robot!",
            style: { marginTop: "10vh" },
          });
        }
        addLogRecord(`joy sent: ${JSON.stringify(joyMsg)}`);
      }
    },
    [channelSend, rtcConnectionState, sendJoy, messageApi, addLogRecord, notifyError]
  );

  const handleJoyKeyboardTopicCreated = useCallback(
    (joyValue, quite = false) => {
      if (!channelSend) {
        notifyError("Data channel is not ready!");
        return;
      }

      const success = sendJoy(joyValue);

      if (success) {
        if (!quite) {
          messageApi.open({
            type: "info",
            content: "Command has been sent to robot!",
            style: { marginTop: "10vh" },
          });
        }
        addLogRecord(`joy sent: ${JSON.stringify(joyValue)}`);
      }
    },
    [channelSend, sendJoy, messageApi, addLogRecord, notifyError]
  );

  const robotInfo = useMemo(
    () => ({
      robot_id: robot?.robot_id ?? id,
      icon_path: getRobotIconPath(robot),
      name: robot?.name ?? id,
      robot_type: robot?.robot_type?.name ?? "Unknown",
    }),
    [robot]
  );

  const connectionsStatus = useMemo(
    () => ({
      ServerConnectionStatus:
        connectionState === CONNECTION_STATES.CONNECTED
          ? "connected"
          : "disconnected",
      PeerConnectionStatus: rtcConnectionState,
      RobotOnlineStatus: robotOnlineStatus,
    }),
    [connectionState, CONNECTION_STATES.CONNECTED, rtcConnectionState, robotOnlineStatus]
  );

  return (
    <div className="robot-control-body">
      <div className="status-panel">
        {contextHolder}
        <RobotStatusViewer
          RobotStatus={robotStatus}
          RobotInfo={robotInfo}
        />
        <audio
          ref={audioAlarm}
          id="alarm-sound"
          src={PATH_TO_WARNING_AUDIO}
          loop
        />
      </div>

      <div className="jitter-panel">
        <JitterGraph />
      </div>

      <div className="cameras-panel">
        {peerConnections &&
          peerConnections.map((peer) => {
            const enableStats = peer?.has_camera || false;
            const allowLocalAudio = peer?.has_speaker || false;
            const allowRemoteAudio = peer?.has_microphones || false;
            const allowRemoteVideo = peer?.has_camera || false;
            const isReceiveAudio = peer?.has_microphones || false;
            const isSendingAudio = peer?.has_speaker || false;

            if (!peer.id) {
              logger.warn("Peer ID is null or undefined:", peer);
            }            

            if (peer.id === controlPeerId) {
              if (peer.has_camera) {
                return (
                  <div className="camera" key={peer.id}>
                    <PeerTileLabel label={peer.id} />
                    <RobotPeerVideoViewer
                      key={peer.id}
                      PeerID={peer.id}
                      ShowDelay={true}
                      ShowStatus={true}
                      Width="100%"
                      Height="100%"
                    />
                  </div>
                );
              }
            } else {
              if (peer.has_camera) {
                return (
                  <RobotPeerConnectionProvider
                    key={peer.id}
                    PeerId={peer.id}
                    StatsEnabled={enableStats}
                    allowLocalAudio={allowLocalAudio}
                    allowRemoteAudio={allowRemoteAudio}
                    allowRemoteVideo={allowRemoteVideo}
                    isReceiveAudio={isReceiveAudio}
                    isSendingAudio={isSendingAudio}
                    trickleICE={true}
                    disableChannelMessageState={true}
                    dataChannels={["chat"]}
                  >
                    <div className="camera" key={peer.id}>
                      <PeerTileLabel label={peer.id} />
                      <RobotPeerVideoViewer
                        key={peer.id}
                        PeerID={peer.id}
                        ShowDelay={peer?.has_camera ? true : false}
                      />
                    </div>
                  </RobotPeerConnectionProvider>
                );
              } else if (peer.has_microphones || peer.has_speaker) {
                return (
                  <RobotPeerConnectionProvider
                    key={peer.id}
                    PeerId={peer.id}
                    StatsEnabled={enableStats}
                    allowLocalAudio={allowLocalAudio}
                    allowRemoteAudio={allowRemoteAudio}
                    allowRemoteVideo={allowRemoteVideo}
                    isReceiveAudio={isReceiveAudio}
                    isSendingAudio={isSendingAudio}
                    trickleICE={true}
                    disableChannelMessageState={true}
                    dataChannels={["chat"]}
                  >
                    <div className="hiden-camera" key={peer.id}>
                      <PeerTileLabel label="audio" />
                      <RobotPeerVideoViewer
                        key={peer.id}
                        PeerID={peer.id}
                        ShowDelay={peer?.has_camera ? true : false}
                      />
                    </div>
                  </RobotPeerConnectionProvider>
                );
              } else {
                return "";
              }
            }
          })}
      </div>

      <div className="control-panel">
        <div className="control-header">
          <div className="control-mode-section">
            <label className="control-label">Control Input Mode:</label>
            <Select
              value={controlMode}
              onChange={(value) => setControlMode(value)}
              style={{ width: 200 }}
              options={[
                {
                  value: "twist",
                  label: <span>⌨️ Keyboard Twist</span>,
                },
                {
                  value: "joy",
                  label: <span>🎮 Keyboard Joy</span>,
                },
                {
                  value: "gamepad",
                  label: <span>🕹️ Joystick Joy</span>,
                },
              ]}
            />
          </div>
          <div className="connection-indicator">
            <span
              className={`status-dot ${
                rtcConnectionState === "connected"
                  ? "connected"
                  : "disconnected"
              }`}
            />
            <span className="status-text">
              {rtcConnectionState === "connected"
                ? "Connected"
                : "Disconnected"}
            </span>
          </div>
        </div>

        <div className="control-interface-section">
          {controlMode === "gamepad" && (
            <div className="gamepad-container">
              <GamepadController
                onGamepadStateUpdated={(joyMsg) => {
                  handleOnJoystickCommandCreated(joyMsg, true);
                }}
                showVirtualJoystick={true}
                joyControlMapping={joyControlMapping}
              />
            </div>
          )}
          {controlMode === "twist" && (
            <div className="keyboard-container">
              <TwistKeyboardMoveCommander
                EnableKeyboard={true}
                OnCommandCreated={handleOnCommandCreated}
              />
            </div>
          )}
          {controlMode === "joy" && (
            <div className="keyboard-container">
              <JoyKeyboardTopicCommander
                OnCommandCreated={handleJoyKeyboardTopicCreated}
                EnableKeyboard={true}
                ThrottleInterval={80}
                JoyControlMapping={joyControlMapping}
              />
            </div>
          )}
        </div>
      </div>

      <div
        className="log-panel"
        style={{
          overflow: "hidden",
        }}
      >
        Log messages:
        <LogViewer
          LogRecords={connectionLogRecords}
          displaySize={logDisplaySize}
        />
      </div>

      <div className="stt-panel">
        <div className="stt-input-row">
          <label style={{ color: "#fff", whiteSpace: "nowrap", margin: 0 }}>
            🎤 Instruction:
          </label>
          <Select
            value={sttLang}
            onChange={(val) => setSttLang(val)}
            disabled={sttIsRecording}
            size="small"
            className="stt-lang-select"
            options={[
              { value: "ko-KR", label: "KR" },
              { value: "en-US", label: "EN" },
              { value: "vi-VN", label: "VN" },
            ]}
          />
          <Input
            placeholder={sttIsRecording ? (sttInterimText || "Listening...") : "Type or record a message..."}
            value={sttText + (sttInterimText ? ` [${sttInterimText}]` : "")}
            onChange={(e) => {
              if (!sttIsRecording) setSttText(e.target.value);
            }}
            onPressEnter={() => handleSendInstruction(sttText)}
            style={{ flex: 1 }}
            suffix={
              isSpeechSupported ? (
                <Tooltip title={sttIsRecording ? "Stop recording (press P to stop)" : `Start speech to text (press P to start or hold \` to push-to-talk)`}>
                  <Button
                    type={sttIsRecording ? "primary" : "text"}
                    danger={sttIsRecording}
                    size="small"
                    icon={sttIsRecording ? <StopOutlined /> : <AudioOutlined />}
                    onClick={sttToggleRecording}
                    style={{ border: "none", boxShadow: "none" }}
                  />
                </Tooltip>
              ) : null
            }
          />
          <Tooltip title={rtcConnectionState !== "connected" ? "Robot not connected" : "Send instruction to robot (or press \\ to send)"}>
            <Button
              type="primary"
              shape="circle"
              icon={<SendOutlined />}
              className="stt-send-btn"
              disabled={!sttText.trim() || rtcConnectionState !== "connected"}
              onClick={() => handleSendInstruction(sttText)}
            />
          </Tooltip>
          <Tooltip title="Clear text box (or press ] to clear)">
            <Button
              shape="circle"
              icon={<CloseOutlined />}
              className="stt-reset-btn"
              disabled={!sttText.trim() && !sttIsRecording}
              onClick={handleResetInstruction}
            />
          </Tooltip>
        </div>
        {lastSentText && (
          <div className="stt-last-sent">
            <span className="stt-last-sent-label">Last sent:</span>
            <span className="stt-last-sent-text">{lastSentText}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RobotGroupControlPage;
